import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Breadcrumb from "../../../component/breadcrumb";
import axiosInstance from "../../../configuration/request";
import { useSnackbar } from "../../../component/SnackbarContext";
import { BsBugFill } from "react-icons/bs";
import {
  formatIncidentStatusLabel,
  getIncidentStatusBadgeClass,
  INCIDENT_STATUSES_ALL,
  INCIDENT_STATUS_VALUES,
} from "./incidentStatus";

const BCrumb = [
  { to: "/app/delivery-incedents", title: "Incident Reports" },
  { title: "Incident Detail" },
];

function normalizeIncidentStatus(raw) {
  const v = (raw || "").toString().trim().toLowerCase();
  return INCIDENT_STATUS_VALUES.includes(v) ? v : "pending";
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function IncidentDetail() {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const showSnackbar = useSnackbar();
  const [incident, setIncident] = useState(location.state?.incident ?? null);
  const [loading, setLoading] = useState(!location.state?.incident);
  const [selectedStatus, setSelectedStatus] = useState(
    normalizeIncidentStatus(location.state?.incident?.status)
  );
  const [statusSaving, setStatusSaving] = useState(false);

  useEffect(() => {
    if (location.state?.incident) {
      setIncident(location.state.incident);
      setSelectedStatus(normalizeIncidentStatus(location.state.incident.status));
      setLoading(false);
      return;
    }
    if (!incidentId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const byId = await axiosInstance.get(
          `/customer/delivery-person/incidents/${incidentId}`
        );
        const payload = byId?.data;
        const row = payload?.data ?? payload;
        if (!cancelled && row && typeof row === "object" && !Array.isArray(row)) {
          setIncident(row);
          setSelectedStatus(normalizeIncidentStatus(row.status));
          setLoading(false);
          return;
        }
      } catch {
        /* try list fallback */
      }

      try {
        const yearAgo = new Date();
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        const now = new Date();
        const params = new URLSearchParams({
          dateFrom: yearAgo.toISOString().split("T")[0],
          dateTo: now.toISOString().split("T")[0],
          status: INCIDENT_STATUSES_ALL,
        });
        const listRes = await axiosInstance.get(
          `/customer/delivery-person/incidents?${params}`
        );
        const body = listRes?.data;
        const rows = Array.isArray(body) ? body : body?.data ?? [];
        const found = rows.find(
          (r) => String(r?.id) === String(incidentId)
        );
        if (!cancelled) {
          if (found) {
            setIncident(found);
            setSelectedStatus(normalizeIncidentStatus(found.status));
          } else showSnackbar("Incident not found.", "error");
        }
      } catch (e) {
        if (!cancelled) {
          showSnackbar(
            e?.response?.data?.message || "Failed to load incident.",
            "error"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [incidentId, location.state?.incident, showSnackbar]);

  const dp = incident?.deliveryPerson || {};
  const user = dp?.user || {};

  const statusClass = getIncidentStatusBadgeClass(incident?.status);
  const currentNormalized = normalizeIncidentStatus(incident?.status);
  const statusDirty = selectedStatus !== currentNormalized;

  const handleStatusUpdate = async () => {
    if (!incident?.id || !statusDirty) return;
    setStatusSaving(true);
    try {
      await axiosInstance.patch(
        `/customer/delivery-person/incidents/${incident.id}/status?status=${encodeURIComponent(
          selectedStatus
        )}`
      );
      setIncident((prev) => (prev ? { ...prev, status: selectedStatus } : prev));
      showSnackbar("Status updated", "success");
    } catch (e) {
      showSnackbar(e?.response?.data?.message || "Failed to update status", "error");
    } finally {
      setStatusSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 md:px-6 py-10 flex flex-col items-center justify-center gap-3">
        <CircularProgress size={40} sx={{ color: "var(--primary-color)" }} />
        <p className="text-sm text-[var(--light-text-color)]">Loading incident…</p>
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="px-4 md:px-6 py-6">
        <Breadcrumb title="" items={BCrumb} />
        <div className="mt-8 text-center text-[var(--light-text-color)]">
          Unable to load this incident.
        </div>
        <button
          type="button"
          className="mt-4 mx-auto block text-[var(--primary-color)] font-medium"
          onClick={() => navigate("/app/delivery-incedents")}
        >
          Back to list
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 pb-10">
      <div className="py-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/app/delivery-incedents")}
          className="inline-flex items-center gap-1 text-[var(--primary-color)] hover:underline text-sm font-medium"
        >
          <ArrowBackIcon fontSize="small" />
          Back
        </button>
        <Breadcrumb title="" items={BCrumb} />
      </div>

      <div className="flex items-start gap-3 mb-6">
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[var(--table-header-bg)] text-[var(--primary-color)] shrink-0">
          <BsBugFill className="text-2xl" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 gap-y-1">
            <h1 className="text-2xl font-semibold text-[var(--primary-text-color)] truncate">
              {incident.title || "Incident"}
            </h1>
            <span
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusClass}`}
            >
              {formatIncidentStatusLabel(incident.status)}
            </span>
          </div>
          <p className="text-sm text-[var(--light-text-color)] mt-1">
            Incident ID #{incident.id} · Reported {formatDate(incident.createdAt)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-2xl border border-[var(--table-border)] bg-[var(--primary-bg-color)] p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-[var(--label-color)] uppercase tracking-wide mb-2">
              Description
            </h2>
            <p className="text-[var(--primary-text-color)] whitespace-pre-wrap leading-relaxed">
              {incident.description || "—"}
            </p>
          </section>

          {incident.photoUrl && (
            <section className="rounded-2xl border border-[var(--table-border)] bg-[var(--primary-bg-color)] p-5 shadow-sm overflow-hidden">
              <h2 className="text-sm font-semibold text-[var(--label-color)] uppercase tracking-wide mb-3">
                Photo
              </h2>
              <a
                href={incident.photoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <img
                  src={incident.photoUrl}
                  alt="Incident attachment"
                  className="rounded-xl max-h-[420px] w-full object-contain bg-black/5"
                />
              </a>
              <p className="text-xs text-[var(--light-text-color)] mt-2">
                Click image to open in a new tab
              </p>
            </section>
          )}
        </div>

        <div className="space-y-6">
          <section className="rounded-2xl border border-[var(--table-border)] bg-[var(--primary-bg-color)] p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-[var(--label-color)] uppercase tracking-wide mb-4">
              Update status
            </h2>
            <label className="block text-xs text-[var(--light-text-color)] mb-2">
              Admin workflow: Pending → Reviewed → Resolved.
            </label>
            <div className="flex flex-col gap-3">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(normalizeIncidentStatus(e.target.value))}
                disabled={statusSaving}
                className="w-full border border-[var(--table-border)] rounded-lg px-3 py-2.5 text-[var(--primary-text-color)] bg-[var(--fields-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              >
                {INCIDENT_STATUS_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {formatIncidentStatusLabel(v).toUpperCase()}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={!statusDirty || statusSaving}
                onClick={handleStatusUpdate}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--primary-color)] text-white px-4 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {statusSaving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Save status"
                )}
              </button>
            </div>
          </section>

          <section className="rounded-2xl border border-[var(--table-border)] bg-[var(--primary-bg-color)] p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-[var(--label-color)] uppercase tracking-wide mb-4">
              Delivery partner
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-[var(--light-text-color)]">Name</dt>
                <dd className="font-medium text-[var(--primary-text-color)]">
                  {user.name || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--light-text-color)]">Email</dt>
                <dd className="font-medium text-[var(--primary-text-color)] break-all">
                  {user.email || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--light-text-color)]">Phone</dt>
                <dd className="font-medium text-[var(--primary-text-color)]">
                  {user.phone_number || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--light-text-color)]">Delivery person ID</dt>
                <dd className="font-medium text-[var(--primary-text-color)]">
                  {dp.id ?? "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--light-text-color)]">Vehicle</dt>
                <dd className="font-medium text-[var(--primary-text-color)]">
                  {[dp.vehicle_type, dp.vehicle_number].filter(Boolean).join(" · ") || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--light-text-color)]">Payout freeze</dt>
                <dd className="font-medium text-[var(--primary-text-color)]">
                  {dp.payoutFreeze === true ? "Yes" : dp.payoutFreeze === false ? "No" : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--light-text-color)]">Stripe account</dt>
                <dd className="font-mono text-xs break-all text-[var(--primary-text-color)]">
                  {dp.stripeAccountId || "—"}
                </dd>
              </div>
            </dl>
          </section>

          {Array.isArray(dp.uploadDoc) && dp.uploadDoc.length > 0 && (
            <section className="rounded-2xl border border-[var(--table-border)] bg-[var(--primary-bg-color)] p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-[var(--label-color)] uppercase tracking-wide mb-3">
                Driver documents
              </h2>
              <ul className="space-y-2 text-sm">
                {dp.uploadDoc.map((doc, i) => (
                  <li key={`${doc.name}-${i}`}>
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--primary-color)] hover:underline"
                    >
                      {doc.name || `Document ${i + 1}`}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
